/**
 * THE ANALYTICS IDENTITY SPINE.
 *
 * One man, one id, from the first thing he types in /apply through account
 * creation, booking, purchase and delivery. Everything in this file is the
 * plumbing that keeps that id the same, plus the browser session id that says
 * which visit produced a given lead.
 *
 * THE ID IS A GENERATED UUID AND NEVER THE EMAIL ADDRESS. An email is personal
 * data, it belongs in a person property rather than a join key, and it changes:
 * men correct a typo in it halfway through the form, which is exactly the
 * moment an email-keyed identity would split in two.
 *
 * IT IS ALSO NOT `posthog_distinct_id`, and that is the whole reason it exists.
 * A distinct id is not stable for a person. PostHog swaps the anonymous id for
 * the Supabase uuid when he signs in, a second device mints its own, and
 * clearing site data mints a third. So the one column tying a lead row to
 * analytics changes under the same man at least three ordinary ways, and every
 * server-side event fired later inherits whichever value was stored last.
 *
 * WHAT THIS FILE MAY IMPORT: nothing server-only. The browser reads the same
 * cookie names and the same validator, so a value the server refuses can never
 * be one the browser cheerfully wrote. The database side lives beside it in
 * `analytics-identity-db.ts`.
 */

/**
 * The first-party cookie the browser writes with its current PostHog session
 * id, read by any route that wants to stamp a server-side event with the
 * session (and therefore the session replay) that produced the request.
 *
 * A cookie rather than a request field because it reaches EVERY route without
 * each one having to be taught, and because the session id is a per-visit fact
 * about the browser rather than a property of one form. It is deliberately not
 * httpOnly: the browser is the writer.
 */
export const PH_SESSION_COOKIE = "ca_ph_session"

/**
 * The first-party cookie the server writes back with the adopted person id, so
 * the browser can stamp it onto the PostHog person without a second request.
 *
 * Readable by scripts on purpose, for the same reason PostHog's own distinct-id
 * cookie is. It carries an opaque uuid and nothing else.
 */
export const ANALYTICS_PERSON_COOKIE = "ca_person"

/** 400 days, the ceiling browsers now clamp a cookie to anyway. The person id
 * is durable by definition, so anything shorter would split one man in two the
 * first time he came back after it lapsed. */
export const ANALYTICS_PERSON_COOKIE_MAX_AGE = 400 * 24 * 60 * 60

/**
 * Both ids are uuids, and both arrive from the client, so both are validated
 * rather than trusted.
 *
 * The session id matters most here. It is written by a script and therefore by
 * anyone, and PostHog treats `$session_id` as the key that binds an event to a
 * recorded session. An unvalidated value lets a visitor attach our server-side
 * booking and sale events to a session that is not his, which corrupts exactly
 * the join this spine exists to make.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function normalizeUuid(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined
  const trimmed = value.trim().toLowerCase()
  return UUID_RE.test(trimmed) ? trimmed : undefined
}

/** A PostHog session id, or undefined when the value is absent or malformed.
 * PostHog mints session ids as uuids, so anything else is not one of ours. */
export function normalizePosthogSessionId(value: unknown): string | undefined {
  return normalizeUuid(value)
}

/** An analytics person id, or undefined. Applied to values coming back from
 * the database and to the cookie the browser reads, so a hand-edited cookie
 * cannot make the browser claim an identity. */
export function normalizeAnalyticsPersonId(value: unknown): string | undefined {
  return normalizeUuid(value)
}

/**
 * NOTHING MINTS A PERSON ID. The person id is the canonical prospect's own id,
 * adopted onto the row by `analytics-identity-db.ts`, because a
 * `coaching_applications` row is an application ATTEMPT and minting per row
 * gave one man as many identities as he had attempts. If you are about to add
 * a `randomUUID()` here, that is the error you are recreating.
 */

/**
 * The session column for a lead write, OMITTED when this save carries no
 * session id rather than written as null.
 *
 * The same law `entryColumns` follows, for the same reason. These land in an
 * UPDATE that runs on every step save, so a null would not mean "unknown", it
 * would overwrite what an earlier save already learned. The trigger is
 * ordinary: a man abandons, comes back through the resume link in a browser
 * where PostHog is blocked, and the save that carries no session id would erase
 * the session that actually produced him.
 */
export function posthogSessionColumn(value: unknown): { posthog_session_id?: string } {
  const sessionId = normalizePosthogSessionId(value)
  return sessionId ? { posthog_session_id: sessionId } : {}
}
